import { FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box, Grid, Typography } from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { Button, IconButton, Select } from '@atoms';
import { usePartnerCustomers } from '@hooks';
import { EMPTY_FILTERS, PendingProspectsSearchFormValues } from './constants';
import styles from './styles';
import { STATUS_OPTIONS } from '../constants';

type CustomerOption = {
  id: number;
  legalName: string;
};

interface SearchBoxPendingProspectsProps {
  filters: PendingProspectsSearchFormValues;
  onSearch: (filters: PendingProspectsSearchFormValues) => void;
}

/**
 * Search box to filter pending prospect registrations
 */
const SearchBoxPendingProspects: FC<SearchBoxPendingProspectsProps> = ({ filters, onSearch }) => {
  const { t } = useTranslation();
  const {
    data: customersData,
    isLoading: isLoadingCustomers,
    error: customersError,
  } = usePartnerCustomers();

  const formik = useFormik<PendingProspectsSearchFormValues>({
    initialValues: filters,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSearch({
        status: values.status,
        customerId: values.customerId,
      });
    },
  });

  const { values, handleBlur, handleChange, handleSubmit, resetForm } = formik;

  /**
   * Clears the search form and resets the applied filters
   */
  const handleClearFilters = () => {
    resetForm({ values: EMPTY_FILTERS });
    onSearch(EMPTY_FILTERS);
  };

  /**
   * Handles the form submit event
   * @param event - The form submit event
   */
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit();
  };

  return (
    <Box sx={styles.searchContainer} component='form' onSubmit={handleFormSubmit}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('PENDING_PROSPECTS:SEARCH_TITLE')}</Typography>
      </Box>
      <Box sx={styles.content}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} md={4}>
            <Select
              label={t('COMMON:COMPANY')}
              name='customerId'
              value={values.customerId}
              onChange={handleChange}
              onBlur={handleBlur}
              items={customersData || []}
              isLoading={isLoadingCustomers}
              isError={Boolean(customersError)}
              placeholder='COMMON:SELECT_COMPANY'
              parseOption={(item: CustomerOption) => ({
                label: item.legalName,
                value: String(item.id),
              })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Select
              label={t('COMMON:STATUS')}
              name='status'
              value={values.status}
              onChange={handleChange}
              onBlur={handleBlur}
              items={STATUS_OPTIONS}
              placeholder='COMMON:SELECT_PLACEHOLDER'
              parseOption={(item: { value: string; label: string }) => ({
                label: t(item.label),
                value: item.value,
              })}
            />
          </Grid>

          <Grid item xs={12} md sx={styles.actionsContainer}>
            <Button
              customStyle={styles.clearButton}
              text={t('COMMON:CLEAR_FILTER')}
              color='primary'
              variant='link'
              type='button'
              onClick={handleClearFilters}
            />
            <IconButton
              tooltip='COMMON:SEARCH'
              onClick={() => handleSubmit()}
              IconComponent={IconSearch}
              variant='contained'
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SearchBoxPendingProspects;
